import React from "react";
import { useState, useEffect } from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  rating?: number;
  reviewCount?: number;
  discount?: number;
  badge?: string;
}

interface BaseBannerProps {
  products: Product[];
  width: number;
  height: number;
  autoSlide?: boolean;
  slideInterval?: number;
  children: (
    product: Product,
    currentIndex: number,
    totalProducts: number
  ) => React.ReactNode;
  showNavigation?: boolean;
  showDots?: boolean;
}

export const BaseBanner: React.FC<BaseBannerProps> = ({
  products,
  width,
  height,
  autoSlide = true,
  slideInterval = 5000,
  children,
  showNavigation = true,
  showDots = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  useEffect(() => {
    if (autoSlide && products.length > 1) {
      const interval = setInterval(() => {
        handleSlideChange((prev) => (prev + 1) % products.length);
      }, slideInterval);
      return () => clearInterval(interval);
    }
  }, [autoSlide, slideInterval, products.length]);

  const handleSlideChange = (
    newIndexOrFunction: number | ((prev: number) => number)
  ) => {
    setIsTransitioning(true);
    setTimeout(() => {
      if (typeof newIndexOrFunction === "function") {
        setCurrentIndex(newIndexOrFunction);
      } else {
        setCurrentIndex(newIndexOrFunction);
      }
      setIsTransitioning(false);
    }, 150); // Half of the transition duration
  };

  const nextSlide = () => {
    handleSlideChange((prev) => (prev + 1) % products.length);
  };

  const prevSlide = () => {
    handleSlideChange((prev) => (prev - 1 + products.length) % products.length);
  };

  const goToSlide = (index: number) => {
    if (index !== currentIndex) {
      handleSlideChange(index);
    }
  };

  if (products.length === 0) return null;

  return (
    <Box
      mx="auto"
      sx={{
        maxWidth: `${width}px`,
        height: `${height}px`,
        position: "relative",
        overflow: "hidden",
        borderRadius: 1,
        boxShadow: 3,
        background: "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
        cursor: "pointer",
        "&:hover": {
          boxShadow: 6,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          transform: isTransitioning ? "translateX(-10px)" : "translateX(0)",
          opacity: isTransitioning ? 0.7 : 1,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {children(products[currentIndex], currentIndex, products.length)}
      </Box>

      {isTransitioning && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)",
            transform: "translateX(-100%)",
            animation: "slideShine 0.3s ease-out",
            "@keyframes slideShine": {
              "0%": {
                transform: "translateX(-100%)",
              },
              "100%": {
                transform: "translateX(100%)",
              },
            },
          }}
        />
      )}

      {products.length > 1 && showNavigation && width > 200 && !isMobile && (
        <>
          <IconButton
            disabled={isTransitioning}
            onClick={prevSlide}
            sx={{
              position: "absolute",
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.8)",
              width: 24,
              height: 24,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.9)",
                transform: "translateY(-50%) scale(1.1)",
              },
              "&:disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton
            disabled={isTransitioning}
            onClick={nextSlide}
            sx={{
              position: "absolute",
              right: 14,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.8)",
              width: 24,
              height: 24,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.9)",
                transform: "translateY(-50%) scale(1.1)",
              },
              "&:disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </>
      )}

      {products.length > 1 && showDots && height > 100 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 0.5,
          }}
        >
          {products.map((_, index) => (
            <Box
              key={index}
              onClick={() => goToSlide(index)}
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor:
                  index === currentIndex ? "white" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform: index === currentIndex ? "scale(1.2)" : "scale(1)",
                "&:hover": {
                  backgroundColor: "white",
                  transform: "scale(1.1)",
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
